import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { WebvoucherDataModel, WebshopSettingsDataModel2, WebshopSettingsDMKey, Webvoucher, WebvoucherDeliverySettings, WebvoucherLayoutDataModel } from "../../shared/models";
import { CoreLogService } from "../../services/log.service";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ContentType } from "../../shared/contentTypes";
import { IWebVoucherCustomDetailService, IWebVoucherSettings, IWebVoucher, DeliverySetting, IContinueShoppingDialogData } from "./webVoucherCustomDetail.facade";
import Utils from "../../shared/utils";
import { WebShopService } from "../../services/webShop.service";
import { WebvoucherConfiguration } from "../../services/IWebShopService";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";

export class WebVoucherSettings implements IWebVoucherSettings {
  public get useNumber_of_Persons(): boolean {
    return this._model.number_of_persons_enable;
  }
  /** cache der ermittelte Liefereinstellungen */
  private _delivery_settings: DeliverySetting;
  public get delivery_settings(): DeliverySetting {
    return this._delivery_settings;
  }
  public get delivery_costs(): number {
    return Utils.centsToCurrency(this._model.delivery_price);
  }

  /** Basis Datenmodel */
  private _model: Webvoucher

  public constructor(model: Webvoucher) {
    const me = this;
    me._model = model;

    switch (me._model.delivery_settings) {
      case WebvoucherDeliverySettings.PrintAtHome:
        me._delivery_settings = DeliverySetting.Mail;
        break;
      case WebvoucherDeliverySettings.Post:
        me._delivery_settings = DeliverySetting.Post;
        break;
      case WebvoucherDeliverySettings.PrintAtHomeOrPost:
        me._delivery_settings = DeliverySetting.MailOrPost;
        break;
      default:
        // delivery_settings is unknown
        // todo error log
        console.error("Unknown delivery_settings", me._model.delivery_settings)
        me._delivery_settings = DeliverySetting.Mail;
        break;
    }
  }
}

export class WebVoucherCustom implements IWebVoucher {
  public get id(): number {
    return this._model.id;
  }
  public get label(): string {
    return this._model.label;
  }
  public get price(): number {
    return this._model.is_free_amount ? 0 : this._model.fix_amount;
  }
  public get hasImage(): boolean {
    return this._model.last_image_update_at_ymd > 0;
  }
  public get image(): string {
    return this._model.image_photo;
  }
  public get description_short(): string {
    return this._model.short_description;
  }
  public get description_long_html(): string {
    return this._model.long_description_html;
  }
  public get has_preview(): boolean {
    return this._layout_model && this._layout_model.last_image_update_at_ymd > 0;
  }
  public get preview(): any {
    return this._layout_model && this._layout_model.image_photo;
  }

  private _model: WebvoucherDataModel
  /** Basis Datenmodel */
  public get model(): WebvoucherDataModel {
    return this._model;
  }

  private _layout_model: WebvoucherLayoutDataModel
  /** Layout Datenmodel */
  public get layout_model(): WebvoucherLayoutDataModel {
    return this._layout_model;
  }

  public constructor(model: WebvoucherDataModel, layout_model: WebvoucherLayoutDataModel) {
    const me = this;
    me._model = model;
    me._layout_model = layout_model;
  }
}

@Injectable()
export class WebVoucherCustomDetailService implements IWebVoucherCustomDetailService, OnDestroy {

  /** status über den initialisierungs zustand */
  private _init: boolean = false;

  // observer
  private _webshopSettingsObsServer: IObjectObserver<WebshopSettingsDataModel2>;
  private _webvoucherCustomObsServer: IObjectObserver<WebvoucherDataModel>;
  private _webvoucherLayoutObsServer: IObjectObserver<WebvoucherLayoutDataModel>;

  public readonly NO_PRICE: number = 0;
  public readonly MIN_PRICE: number = 1.00;
  public readonly MAX_PRICE: number = 9999.99;
  public readonly MIN_COUNT: number = 1;
  public readonly MAX_COUNT: number = 99;
  public readonly MIN_PERSON_COUNT: number = 1;
  public readonly MAX_PERSON_COUNT: number = 99;

  constructor(private _coreLogService: CoreLogService,
    private _webShopService: WebShopService,
    private _bottomSheetHelperService: BottomSheetHelperService,
    objObsInjectHelper: CircularInjectionHelper) {
    const me = this;

    me._webshopSettingsObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, objObsInjectHelper);
    // _webshopSettingsObsServer wird nicht auf änderungen beobachtet, um den Gast nicht beim Shoppen zu stören

    me._webvoucherCustomObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebvoucherDataModel>(ContentType.Webvoucher_Custom, objObsInjectHelper);
    // _webvoucherCustomObsServer wird nicht auf änderungen beobachtet, um den Gast nicht beim Shoppen zu stören

    me._webvoucherLayoutObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebvoucherLayoutDataModel>(ContentType.Webvoucher_Layout_Preview, objObsInjectHelper);
    // _webvoucherLayoutObsServer wird nicht auf änderungen beobachtet, um den Gast nicht beim Shoppen zu stören
  }

  ngOnDestroy(): void {
    // keine freigabe notwendig
  }

  /** hält die referenz auf die Webgutschien landingpage */
  private _webVoucher_settings: WebVoucherSettings = null;
  private _webVoucher_settingsSubject: BehaviorSubject<IWebVoucherSettings> = new BehaviorSubject<IWebVoucherSettings>(null);
  public get webVoucher_settings$(): Observable<IWebVoucherSettings> {
    return this._webVoucher_settingsSubject.asObservable();
  }

  /** hält die referenz auf den Webgutschien */
  private _webVoucher: WebVoucherCustom = null;
  private _webVoucherSubject: BehaviorSubject<IWebVoucher> = new BehaviorSubject<IWebVoucher>(null);
  public get webVoucher$(): Observable<IWebVoucher> {
    return this._webVoucherSubject.asObservable();
  }

  private _price: number = this.NO_PRICE;
  private _priceSubject: BehaviorSubject<number> = new BehaviorSubject<number>(this.NO_PRICE);
  public get price$(): Observable<number> {
    return this._priceSubject.asObservable();
  }

  public changePrice(price: number): void {
    const me = this;
    price = Utils.moneyRound(price);
    if (me._init && me._webVoucher && me._webVoucher.model.is_free_amount) {
      price = Math.min(Math.max(price, me.MIN_PRICE), me.MAX_PRICE);
      if (me._price != price) {
        me._price = price
        me._priceSubject.next(me._price);
        me.recalculateSum();
      }
    }
  }

  private _deliveryType: DeliverySetting = DeliverySetting.Mail;
  private _deliveryTypeSubject: BehaviorSubject<DeliverySetting> = new BehaviorSubject<DeliverySetting>(DeliverySetting.Mail);
  public get deliveryType$(): Observable<DeliverySetting> {
    return this._deliveryTypeSubject.asObservable();
  }

  public changeDeliveryType(deliveryType: DeliverySetting.Mail | DeliverySetting.Post): void {
    const me = this;
    if (me._init && me._webVoucher_settings && deliveryType != me._deliveryType) {
      let nextDeliveryType = me._deliveryType;
      if (deliveryType == DeliverySetting.Mail && me._webVoucher_settings.delivery_settings != DeliverySetting.Post) {
        nextDeliveryType = deliveryType;
      } else if (deliveryType == DeliverySetting.Post && me._webVoucher_settings.delivery_settings != DeliverySetting.Mail) {
        nextDeliveryType = deliveryType;
      }
      if (nextDeliveryType != me._deliveryType) {
        me._deliveryType = nextDeliveryType;
        me._deliveryTypeSubject.next(me._deliveryType);
      }
    }
  }

  /** hält die referenz auf die Personenanzahl */
  private _personCount: number = this.MIN_PERSON_COUNT;
  private _personCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(this.MIN_PERSON_COUNT);
  public get personCount$(): Observable<number> {
    return this._personCountSubject.asObservable();
  }

  public incrementPersonCount(): void {
    const me = this;
    if (me._init && me._webVoucher_settings && me._webVoucher_settings.useNumber_of_Persons && me._personCount < me.MAX_PERSON_COUNT) {
      me._personCount += 1;
      me._personCountSubject.next(me._personCount);
      me.recalculateSum();
    }
  }

  public decrementPersonCount(): void {
    const me = this;
    if (me._init && me._webVoucher_settings && me._webVoucher_settings.useNumber_of_Persons && me._personCount > me.MIN_PERSON_COUNT) {
      me._personCount -= 1;
      me._personCountSubject.next(me._personCount);
      me.recalculateSum();
    }
  }

  public changePersonCount(personCount: number): void {
    const me = this;
    personCount = Math.min(Math.max(Math.floor(personCount), me.MIN_PERSON_COUNT), me.MAX_PERSON_COUNT);
    if (me._personCount != personCount) {
      me._personCount = personCount
      me._personCountSubject.next(me._personCount);
      me.recalculateSum();
    }
  }

  /** hält die referenz auf die Webgutschien anzahl */
  private _count: number = this.MIN_COUNT;
  private _countSubject: BehaviorSubject<number> = new BehaviorSubject<number>(this.MIN_COUNT);
  public get count$(): Observable<number> {
    return this._countSubject.asObservable();
  }

  public incrementCount(): void {
    const me = this;
    if (me._count < me.MAX_COUNT) {
      me._count += 1;
      me._countSubject.next(me._count);
      me.recalculateSum();
    }
  }

  public decrementCount(): void {
    const me = this;
    if (me._count > me.MIN_COUNT) {
      me._count -= 1;
      me._countSubject.next(me._count);
      me.recalculateSum();
    }
  }

  public changeCount(count: number): void {
    const me = this;
    count = Math.min(Math.max(Math.floor(count), me.MIN_COUNT), me.MAX_COUNT);
    if (me._count != count) {
      me._count = count
      me._countSubject.next(me._count);
      me.recalculateSum();
    }
  }

  /** ermittelt die summer erneut */
  private recalculateSum(): void {
    const me = this;
    let sum = 0;

    if (me._webVoucher && me._webVoucher_settings) {
      sum = Utils.moneyRound(me._price * me._personCount * me._count);
    }

    if (me._sum != sum) {
      me._sum = sum;
      me._sumSubject.next(me._sum);
    }
  }

  /** hält die referenz auf die Webgutschiene */
  private _sum: number = 0;
  private _sumSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get sum$(): Observable<number> {
    return this._sumSubject.asObservable();
  }

  public async init(webvoucherID: number): Promise<void> {
    const me = this;
    if (me._init) {
      throw new Error("WebVoucherSelectorService is already init");
    }

    try {

      const loadSettingPromise = me.loadWebVoucherSettings();
      const loadWebVoucherPromise = me.loadWebVoucher(webvoucherID);
      await Promise.all([
        loadSettingPromise,
        loadWebVoucherPromise
      ]);
      // setze den initialen Werte
      if (me._webVoucher_settings && me._webVoucher_settings.delivery_settings == DeliverySetting.Post) {
        me._deliveryType = DeliverySetting.Post;
        me._deliveryTypeSubject.next(me._deliveryType);
      }
      if (me._webVoucher && me._price != me._webVoucher.price) {
        me._price = me._webVoucher.price;
        me._priceSubject.next(me._price);
      }
      if (me._count != 1) {
        me._count = 1;
        me._countSubject.next(me._count);
      }
      if (me._personCount != 1) {
        me._personCount = 1;
        me._personCountSubject.next(me._personCount);
      }
      me.recalculateSum();
    } catch (error) {
      me._coreLogService.error("WebVoucherSelectorService.loadWebVoucherLandingPage", "catch", error);
    }
    me._init = true;
  }

  /** ermittelt die Webgutschein Landingpage */
  private async loadWebVoucherSettings(): Promise<void> {
    const me = this;
    try {
      const webshopSettings = await me._webshopSettingsObsServer.GetObject(WebshopSettingsDMKey);
      let webVoucher_settings = null;
      if (webshopSettings.webvoucher) {
        webVoucher_settings = new WebVoucherSettings(webshopSettings.webvoucher);
      }
      if (!Utils.IsEqual(me._webVoucher_settings, webVoucher_settings)) {
        me._webVoucher_settings = webVoucher_settings;
        me._webVoucher_settingsSubject.next(me._webVoucher_settings);
      }
    } catch (error) {
      me._coreLogService.error("WebVoucherSelectorService.loadWebVoucherLandingPage", "catch", error);
    }
  }

  /** ermittelt die Webgutscheine */
  private async loadWebVoucher(webvoucherID: number): Promise<void> {
    const me = this;
    try {
      const webvoucher = await me._webvoucherCustomObsServer.GetObject(webvoucherID);
      if (webvoucher) {
        const webvoucherLayout = await me._webvoucherLayoutObsServer.GetObject(webvoucher.layout_id);
        const webVoucherCustom = new WebVoucherCustom(webvoucher, webvoucherLayout);
        if (!Utils.IsEqual(me._webVoucher, webVoucherCustom)) {
          me._webVoucher = webVoucherCustom;
          me._webVoucherSubject.next(me._webVoucher);
        }
      } else {
        me._coreLogService.error("WebVoucherSelectorService.loadWebVoucher", "GetObject fail", webvoucherID);
      }
    } catch (error) {
      me._coreLogService.error("WebVoucherSelectorService.loadWebVoucher", "catch", webvoucherID, error);
    }
  }

  public async addToBasket(): Promise<void> {
    const me = this;

    if (me._price == me.NO_PRICE) {
      return Promise.reject("no price");
    }

    const config: WebvoucherConfiguration = {
      count: me._count,
      show_person_count: me._webVoucher_settings.useNumber_of_Persons,
      personCount: me._personCount,
      show_delivery: me._webVoucher_settings.delivery_settings === DeliverySetting.MailOrPost,
      postDelivery: me._deliveryType === DeliverySetting.Post,
    };

    if (me._webVoucher.model.is_free_amount) {
      config.variable_price = me._price;
    }

    await me._webShopService.addWebvoucherToShippingCart(me._webVoucher.model, config);
  }

  public getDialogConfig() {
    const me = this;
    const webVoucherConfig: IContinueShoppingDialogData = {
      itemConfig: {
        image: me._webVoucher.hasImage ? me._webVoucher.image : "",
        label: me._webVoucher.label,
        price: me._sum,
        count: me._count,
        isWebVoucher: true,
        displayPersonCount: me._webVoucher_settings.useNumber_of_Persons,
        personCount: me._personCount,
        isFreeAmount: me._webVoucher.model.is_free_amount,
        showDeliveryType: me._webVoucher_settings.delivery_settings === DeliverySetting.MailOrPost,
        deliveryType: me._deliveryType,
      },
      isInhOuseGroupOrder: false

    }
    return webVoucherConfig;
  }
}